import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTournament } from '../../hooks/useGetTournament';
import { useUpdateTournamentChallenge } from '../../hooks/useUpdateTournamentChallenge';
import { Loader } from '../Shared/Loader/Loader';
import { GreenButton } from '../Shared/GreenButton/GreenButton';
import { TransparentButton } from '../Shared/TransparentButton/TransparentButton';
import styles from './ChallengeDetail.module.scss';

const toDatetimeLocal = (isoString: string): string => {
    const d = new Date(isoString);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

export const EditChallenge = () => {
    const { id, challengeId } = useParams<{ id: string; challengeId: string }>();
    const navigate = useNavigate();
    const { data: tournament, isLoading } = useGetTournament(Number(id));
    const { mutate: updateChallenge, isLoading: isUpdatingChallenge } = useUpdateTournamentChallenge();

    const decodedChallengeId = decodeURIComponent(challengeId ?? '');
    const challenge = tournament?.challenges?.find((item) => item.challengeId === decodedChallengeId);

    const [scheduledAt, setScheduledAt] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (challenge) {
            setScheduledAt(toDatetimeLocal(challenge.scheduledAt));
        }
    }, [challenge]);

    const handleSave = async () => {
        if (!id || !challenge || !scheduledAt) return;
        setError(null);

        updateChallenge(
            {
                tournamentId: Number(id),
                challengeId: challenge.challengeId,
                scheduledAt: new Date(scheduledAt).toISOString(),
            },
            {
                onSuccess: () => {
                    navigate(`/tournaments/${id}/challenges/${encodeURIComponent(challenge.challengeId)}`);
                },
                onError: (e: any) => {
                    setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to update challenge');
                },
            }
        );
    };

    if (isLoading) {
        return <Loader />;
    }

    if (!tournament || !challenge) {
        return <div className={styles.empty}>Challenge not found</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backButton} onClick={() => navigate(`/tournaments/${id}/challenges/${encodeURIComponent(challenge.challengeId)}`)}>← Back</div>
                <div className={styles.title}>Edit Challenge</div>
            </div>

            <div className={styles.formCard}>
                <div className={styles.formGrid}>
                    <div className={styles.field}>
                        <div className={styles.label}>Challenge ID</div>
                        <input className={styles.input} value={challenge.challengeId} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Tournament ID</div>
                        <input className={styles.input} value={String(challenge.tournamentId)} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Stage</div>
                        <input className={styles.input} value={challenge.stage} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Round</div>
                        <input className={styles.input} value={challenge.roundName} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Capacity</div>
                        <input className={styles.input} value={String(challenge.capacity)} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Auto Win</div>
                        <input className={styles.input} value={challenge.isAutoWin ? 'Yes' : 'No'} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Abandoned</div>
                        <input className={styles.input} value={challenge.isAbandoned ? 'Yes' : 'No'} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Next Challenge ID</div>
                        <input className={styles.input} value={challenge.nextChallengeId || '—'} disabled />
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>Scheduled At</div>
                        <input
                            className={styles.input}
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                        />
                    </div>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.actions}>
                    <TransparentButton outline onClick={() => navigate(`/tournaments/${id}/challenges/${encodeURIComponent(challenge.challengeId)}`)} noMargin>
                        Cancel
                    </TransparentButton>
                    <GreenButton onClick={handleSave} disabled={!scheduledAt || isUpdatingChallenge} noMargin>
                        {isUpdatingChallenge ? 'Saving...' : 'Save'}
                    </GreenButton>
                </div>
            </div>
        </div>
    );
};
